import Highcharts, { format } from 'highcharts';
import Grid from '@highcharts/grid-lite/es-modules/masters/grid-lite.src.js';
import { getSPP, filterByCategory } from '../ranking-sm/data.js';
import { gradientHex } from '../helpers/colors.js';

const REVENUE_COLOR = '#2222e668';
const FINES_COLOR = '#8e44ad';
const MANDATES_COLOR = '#e74c3c';

function buildSPPCityMap() {
    const byCity = {};
    getSPP().forEach(entry => {
        if (!byCity[entry.city]) byCity[entry.city] = {};
        byCity[entry.city][entry.metric] = { value: entry.value };
    });
    return byCity;
}

function fmt(value, decimals, suffix = '') {
    if (value === null || value === undefined) return '–';
    return value.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
    }) + suffix;
}

/**
 * Chart 1: Parking fees and additional fees per resident (stacked bar)
 * @param {string} id
 */
export function chartSPPRevenue(id) {
    if (!document.getElementById(id)) return;

    const sppByCity = buildSPPCityMap();
    const smData = filterByCategory(['Liczba mieszkańców']);
    const popByCity = {};
    smData.forEach(d => { popByCity[d.city] = d['Liczba mieszkańców']; });

    const revenueKey = 'Suma zebranych opłat za postój';
    const finesKey = 'Łączna kwota pobranych opłat za złamanie regulaminu parkowania';

    const rows = Object.entries(sppByCity)
        .filter(([city, metrics]) => metrics[revenueKey] !== undefined && popByCity[city])
        .map(([city, metrics]) => {
            const revenueEntry = metrics[revenueKey];
            const finesEntry = metrics[finesKey];
            const pop = popByCity[city];
            const parking = revenueEntry.value;
            const fines = finesEntry ? finesEntry.value : 0;
            return {
                city,
                parking,
                fines,
                perPersonParking: Math.round(parking / pop),
                perPersonFines: Math.round(fines / pop),
                perPersonTotal: Math.round(parking / pop) + Math.round(fines / pop)
            };
        })
        .sort((a, b) => b.perPersonTotal - a.perPersonTotal);

    const byCity = {};
    rows.forEach(d => { byCity[d.city] = d; });
    const categories = rows.map(d => d.city);

    Highcharts.chart(id, {
        chart: { type: 'bar', backgroundColor: 'transparent' },
        title: { text: 'Parking fee revenue per resident' },
        subtitle: { text: 'No data for Zielona Góra and Lublin.' },
        xAxis: {
            categories,
            title: { text: null },
            labels: { style: { fontSize: '14px' } }
        },
        yAxis: {
            title: { text: null },
            labels: { format: '{value} PLN' },
            stackLabels: {
                enabled: true,
                formatter: function () {
                    return `${this.total.toLocaleString('en-US')} PLN`;
                },
                style: { fontSize: '12px', fontWeight: 'bold', color: '#333' }
            }
        },
        legend: { enabled: true, verticalAlign: 'top', reversed: true },
        tooltip: {
            formatter: function () {
                const d = byCity[this.key];
                if (!d) return false;
                return `<b>${d.city}</b><br>
                    Parking fees: <b>${d.perPersonParking.toLocaleString('en-US')} PLN</b>/resident<br>
                    Additional fees: <b>${d.perPersonFines.toLocaleString('en-US')} PLN</b>/resident<br>
                    Total: <b>${d.perPersonTotal.toLocaleString('en-US')} PLN</b>/resident`;
            }
        },
        plotOptions: {
            bar: { stacking: 'normal', borderRadius: 4, dataLabels: { enabled: false } }
        },
        credits: { enabled: false },
        series: [
            {
                name: 'Additional fees for violations',
                color: FINES_COLOR,
                data: rows.map(d => ({ y: d.perPersonFines, color: FINES_COLOR }))
            },
            {
                name: 'Parking fees',
                color: REVENUE_COLOR,
                data: rows.map(d => ({ y: d.perPersonParking, color: REVENUE_COLOR }))
            }
        ]
    });
}

/**
 * Chart 2: Guard fines (PRD) vs SPP additional fees – absolute values (stacked bar)
 * @param {string} id
 */
export function chartSPPAbsolute(id) {
    if (!document.getElementById(id)) return;

    const sppByCity = buildSPPCityMap();
    const smData = filterByCategory(['Mandaty: wartość wystawionych dot. PRD']);
    const smFinesByCity = {};
    smData.forEach(d => { smFinesByCity[d.city] = d['Mandaty: wartość wystawionych dot. PRD']; });

    const finesKey = 'Łączna kwota pobranych opłat za złamanie regulaminu parkowania';
    const revenueKey = 'Suma zebranych opłat za postój';

    const totals = { sppFines: 0, sppRevenue: 0, smFines: 0 }

    const rows = Object.entries(sppByCity)
        .filter(([city, metrics]) => metrics[finesKey] !== undefined && smFinesByCity[city])
        .map(([city, metrics]) => {
            const sppEntry = metrics[finesKey];
            const sppRevenue = metrics[revenueKey];
            const smFines = smFinesByCity[city];
            totals.sppFines += sppEntry.value
            totals.sppRevenue += sppRevenue.value
            totals.smFines += smFines
            return {
                city,
                sppFines: sppEntry.value,
                sppRevenue: sppRevenue.value,
                smFines,
                total: smFines + sppEntry.value
            };
        })
        .sort((a, b) => b.total - a.total);

    const rowsWithTotals = [...rows]
    rowsWithTotals.push({
        city: "Total",
        ...totals,
        total: totals.smFines + totals.sppFines
    })

    const byCity = {};
    rows.forEach(d => { byCity[d.city] = d; });

    const toMln = v => (Math.round(v / 1e5) / 10).toLocaleString('en-US');

    Grid.grid(id + '-grid', {
        dataTable: {
            columns: {
                "City": rowsWithTotals.map(r => r.city),
                "SPP:<br>parking fees": rowsWithTotals.map(r => `${toMln(r.sppRevenue)} mln`),
                "SPP:<br>additional fees": rowsWithTotals.map(r => `${toMln(r.sppFines)} mln`),
                "Guard fines": rowsWithTotals.map(r => `${toMln(r.smFines)} mln`),
                "Penalty ratio": rowsWithTotals.map(r => `${fmt(r.sppFines / r.smFines, 2, '')}`)
            }
        },
        rendering: {
            rows: { strictHeights: false },
            theme: 'hcg-theme-default theme-compact'
        },
        columns: [
            { id: "City", cells: { className: 'bold' } },
            { id: "SPP:<br>parking fees", cells: { className: 'cell-right' } },
            { id: "SPP:<br>additional fees", cells: { className: 'cell-right' } },
            { id: "Guard fines", cells: { className: 'cell-right' } },
            { id: "Penalty ratio", cells: { className: 'cell-right' } }
        ]
    });

    Highcharts.chart(id, {
        chart: { type: 'bar', backgroundColor: 'transparent' },
        title: { text: 'Guard fines (PRD) and SPP additional fees – total value' },
        subtitle: { text: 'No data for Zielona Góra and Lublin.' },
        xAxis: {
            categories: rows.map(d => d.city),
            title: { text: null },
            labels: { style: { fontSize: '14px' } }
        },
        yAxis: {
            title: { text: null },
            labels: {
                formatter: function () { return `${toMln(this.value)} mln PLN`; }
            },
            stackLabels: {
                enabled: true,
                formatter: function () { return `${toMln(this.total)} mln PLN`; },
                style: { fontSize: '12px', fontWeight: 'bold', color: '#333' }
            }
        },
        legend: { enabled: true, verticalAlign: 'top', reversed: true },
        tooltip: {
            formatter: function () {
                const d = byCity[this.key];
                if (!d) return false;
                return `<b>${d.city}</b><br>
                    Guard fines (PRD): <b>${Math.round(d.smFines).toLocaleString('en-US')} PLN</b><br>
                    SPP additional fees: <b>${Math.round(d.sppFines).toLocaleString('en-US')} PLN</b><br>
                    Total: <b>${Math.round(d.total).toLocaleString('en-US')} PLN</b>`;
            }
        },
        plotOptions: {
            bar: { stacking: 'normal', borderRadius: 4, dataLabels: { enabled: false } }
        },
        credits: { enabled: false },
        series: [
            {
                name: 'SPP additional fees',
                data: rows.map(d => ({ y: d.sppFines })),
                color: FINES_COLOR
            },
            {
                name: 'Guard fines (PRD)',
                color: MANDATES_COLOR,
                data: rows.map(d => ({ y: d.smFines }))
            }
        ]
    });
}

/**
 * Chart 3: SPP penalty fees as a multiple of guard fines
 * @param {string} id
 */
export function chartSPPFinesShare(id) {
    if (!document.getElementById(id)) return;

    const sppByCity = buildSPPCityMap();
    const smData = filterByCategory(['Mandaty: wartość wystawionych dot. PRD']);
    const smFinesByCity = {};
    smData.forEach(d => { smFinesByCity[d.city] = d['Mandaty: wartość wystawionych dot. PRD']; });

    const metricKey = 'Łączna kwota pobranych opłat za złamanie regulaminu parkowania';

    const rows = Object.entries(sppByCity)
        .filter(([city, metrics]) => metrics[metricKey] !== undefined && smFinesByCity[city])
        .map(([city, metrics]) => {
            const entry = metrics[metricKey];
            const smFines = smFinesByCity[city];
            return {
                city,
                sppFines: entry.value,
                smFines,
                ratio: Math.round(entry.value / smFines * 10) / 10
            };
        })
        .sort((a, b) => b.ratio - a.ratio);

    const byCity = {};
    rows.forEach(d => { byCity[d.city] = d; });

    Highcharts.chart(id, {
        chart: { type: 'bar', backgroundColor: 'transparent' },
        title: { text: 'SPP penalty fees vs guard fines' },
        subtitle: { text: 'SPP additional fees / guard fines.' },
        xAxis: {
            categories: rows.map(d => d.city),
            title: { text: null },
            labels: { style: { fontSize: '14px' } }
        },
        yAxis: {
            title: { text: null },
            labels: { format: '{value}' },
            min: 0
        },
        legend: { enabled: false },
        tooltip: {
            formatter: function () {
                const d = byCity[this.key];
                if (!d) return false;
                return `<b>${d.city}</b><br>
                    SPP violation fees: ${Math.round(d.sppFines).toLocaleString('en-US')} PLN<br>
                    Guard fines: ${Math.round(d.smFines).toLocaleString('en-US')} PLN<br>
                    <b>Ratio: ${d.ratio}</b>`;
            }
        },
        plotOptions: {
            bar: {
                borderRadius: 4,
                dataLabels: {
                    enabled: true,
                    formatter: function () { return `${this.y}`; },
                    style: { fontSize: '12px', textOutline: 'none' }
                }
            }
        },
        credits: { enabled: false },
        series: [{
            name: 'SPP additional fees / guard fines',
            type: 'bar',
            yAxis: 0,
            id: 'main',
            color: '#FFFF00',
            data: rows.map(d => ({
                y: d.ratio,
                color: gradientHex('#00881e', '#FF0000', d.ratio / 2)
            }))
        }]
    });
}
